<template>
  <UiComponentWrapper class="page">
    <!-- Section > Hero -->
    <UiComponentContainer class="jumbotron">
      <OrganismHero
        :primary-button="
          props.page.heroDisplayConversionCta
            ? {
              ...signupOrUpgradeCallToAction,
              label: 'Header section CTA',
            }
            : undefined
        "
        :secondary-button="
          props.page.heroDisplayConversionCta
            ? {
              ...requestDemoCallToAction,
              label: 'Header section CTA',
            }
            : undefined
        "
      >
        <template #heading>
          {{ props.page.heroTitle }}
        </template>
        <template #paragraph>
          {{ props.page.heroText }}
        </template>
      </OrganismHero>
      <UiComponentImage
        v-if="heroImage"
        :key="`hero-illustration-${heroImageSize}`"
        :alt="heroImage.alt"
        class="illustration"
        :expected-size="heroImageSize"
        :height="heroImage.height"
        :source="heroImage.url"
        :width="heroImage.width"
      />
    </UiComponentContainer>

    <!-- Section > Search Bar -->
    <UiComponentWrapper class="search">
      <UiComponentContainer class="inner">
        <UiComponentHeading :level="2">
          {{ props.parentPage.searchBarTitle }}
        </UiComponentHeading>
        <UiComponentWrapper class="bar">
          <UiComponentInput
            id="search"
            ref="$input"
            name="search"
            :placeholder="props.parentPage.searchBarPlaceholder"
            size="l"
            type="text"
            @keyup.enter="updateSearchedTerm"
          />
          <UiComponentIcon type="search" />
          <UiComponentIcon
            :class="{ active: searchedTerm !== '' }"
            type="x"
            @click="resetSearchedTerm"
          />
          <AtomButton
            :disabled="$input && $input.getValue().trim() === ''"
            size="l"
            to="#"
            @click="updateSearchedTerm"
          >
            <UiComponentIcon type="chevron-right" />
          </AtomButton>
          <UiComponentSelect
            id="filter"
            :key="`select-${sortingIndex}`"
            ref="$selectFilter"
            :options="sortingOptions"
            has-icon
            icon-type="filter"
            :index="sortingIndex"
            size="l"
            @select:change="updateSorting"
          />
        </UiComponentWrapper>
      </UiComponentContainer>
    </UiComponentWrapper>

    <!-- Section > Main -->
    <UiComponentContainer class="main">
      <UiComponentWrapper class="head">
        <UiComponentWrapper class="filters">
          <UiComponentWrapper class="title">
            <UiComponentHeading
              as="paragraph"
              :level="5"
            >
              {{ props.parentPage.filtersTitle }}
            </UiComponentHeading>
            <NuxtLink
              class="remove"
              :to="localePath('ice-breaker-games')"
            >
              {{ props.parentPage.filtersRemove }}
            </NuxtLink>
          </UiComponentWrapper>
          <UiComponentWrapper class="types">
            <UiComponentWrapper
              v-for="filter in props.allFilters"
              :key="`filter-${filter.id}`"
              class="type"
            >
              <UiComponentParagraph class="name">
                {{ filter.name }}
              </UiComponentParagraph>
              <UiComponentWrapper class="list">
                <template
                  v-for="{ id, tag, slug } in props.allCategories.filter(({ category }) => category.id === filter.id)"
                  :key="`tag-${id}`"
                >
                  <NuxtLink
                    v-if="props.page.id === id"
                    class="item selected"
                    :class="{
                      active: activeFilters.includes(tag),
                    }"
                    :to="
                      activeFilters.includes(tag)
                        ? localePath('ice-breaker-games')
                        : localePath({
                          name: 'ice-breaker-games-slug',
                          params: { slug },
                        })
                    "
                  >
                    {{ tag }}
                  </NuxtLink>
                  <UiComponentWrapper
                    v-else
                    class="item"
                    :class="{
                      active: activeFilters.includes(tag),
                    }"
                    @click="toogleFilter(tag)"
                  >
                    {{ tag }}
                  </UiComponentWrapper>
                </template>
              </UiComponentWrapper>
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>

        <OrganismContentCard
          v-if="!!sidebarCTACard"
          as="paragraph"
          class="resource"
          :content="sidebarCTACard"
        />

        <UiComponentWrapper
          class="questions"
          @click.stop="navigateToIcebreakerQuestions"
        >
          <UiComponentWrapper class="illustration">
            <UiComponentImage
              v-if="props.componentIcebreakerQuestions.image"
              :alt="props.componentIcebreakerQuestions.image.alt"
              :expected-size="202"
              :height="props.componentIcebreakerQuestions.image.height"
              orientation="portrait"
              :source="props.componentIcebreakerQuestions.image.url"
              :width="props.componentIcebreakerQuestions.image.width"
            />
          </UiComponentWrapper>
          <UiComponentWrapper class="metadata">
            <UiComponentHeading
              as="paragraph"
              :level="5"
            >
              {{ props.componentIcebreakerQuestions.title }}
            </UiComponentHeading>
            <AtomButton
              action="Button clicked"
              label="Sidebar section CTA"
              :to="localePath('ice-breaker-questions')"
              type="text"
            >
              {{ props.componentGlobal.learnMore }}
              <UiComponentIcon type="arrow-right" />
            </AtomButton>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper class="body">
        <template v-if="allMatchingIcebreakers.length === 0">
          <UiComponentWrapper class="empty">
            <UiComponentWrapper class="inner">
              <UiComponentImage
                :alt="props.parentPage.noMatchText"
                :expected-size="190"
                :height="570"
                orientation="portrait"
                source="https://livestorm.imgix.net/1127/1644934630-empty-state.png"
                :width="490"
              />
              <UiComponentParagraph>{{ props.parentPage.noMatchTitle }}</UiComponentParagraph>
              <UiComponentParagraph>{{ props.parentPage.noMatchText }}</UiComponentParagraph>
            </UiComponentWrapper>
          </UiComponentWrapper>
        </template>
        <template v-else>
          <MoleculeIceBreakerCard
            v-for="{ id, color, duration, pattern, title, text, slug } in allMatchingIcebreakers"
            :key="`matching-icebreaker-${id}`"
            class="card"
            :color="color"
            :duration="duration"
            :pattern="pattern"
            :title="title"
            :text="text"
            :slug="slug"
          />
        </template>
      </UiComponentWrapper>
      <UiComponentWrapper class="footer">
        <OrganismContentCard
          v-if="!!sidebarCTACard"
          as="paragraph"
          class="resource"
          :content="sidebarCTACard"
        />

        <UiComponentWrapper class="questions">
          <UiComponentWrapper class="illustration">
            <UiComponentImage
              v-if="props.componentIcebreakerQuestions.image"
              :key="`questions-illustration-${screenWidth < 768 ? 202 : 240}`"
              :alt="props.componentIcebreakerQuestions.image.alt"
              :expected-size="screenWidth < 768 ? 202 : 240"
              :height="props.componentIcebreakerQuestions.image.height"
              orientation="portrait"
              :source="props.componentIcebreakerQuestions.image.url"
              :width="props.componentIcebreakerQuestions.image.width"
            />
          </UiComponentWrapper>
          <UiComponentWrapper class="metadata">
            <UiComponentHeading
              as="paragraph"
              :level="5"
            >
              {{ props.componentIcebreakerQuestions.title }}
            </UiComponentHeading>
            <AtomButton
              :to="localePath('ice-breaker-questions')"
              type="text"
            >
              {{ props.componentGlobal.learnMore }}
              <UiComponentIcon type="arrow-right" />
            </AtomButton>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Use Cases -->
    <UiComponentContainer class="use-cases">
      <UiComponentHeading :level="2">
        {{ props.parentPage.allUseCasesTitle }}
      </UiComponentHeading>
      <UiComponentWrapper class="list">
        <AtomAnchor
          v-for="{ id, heroShortTitle, slug } in props.allCategories"
          :key="`category-${id}`"
          class="item"
          :to="localePath({
            name: 'ice-breaker-games-slug',
            params: { slug },
          })"
        >
          {{ heroShortTitle }}
        </AtomAnchor>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > How To -->
    <UiComponentContainer class="how-to">
      <UiComponentHeading :level="2">
        {{ props.componentStepByStep.title }}
      </UiComponentHeading>
      <UiComponentWrapper class="list">
        <UiComponentWrapper
          v-for="{ id, icon, title, text } in props.componentStepByStep.steps"
          :key="`item-${id}`"
          class="item"
        >
          <UiComponentWrapper class="head">
            <UiComponentIcon :type="icon.name" />
          </UiComponentWrapper>
          <UiComponentWrapper class="body">
            <UiComponentHeading :level="3">
              {{ title }}
            </UiComponentHeading>
            <UiComponentParagraph>{{ text }}</UiComponentParagraph>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Get Started -->
    <UiComponentContainer class="get-started">
      <OrganismCallToAction
        :features="livestormFeatures"
        :primary-button="{
          ...signupOrUpgradeCallToAction,
          label: 'Big section CTA',
        }"
        :secondary-button="{
          ...requestDemoCallToAction,
          label: 'Big section CTA',
        }"
      >
        <template #heading>
          {{ props.componentGetStarted.title }}
        </template>
        <template #paragraph>
          {{ props.componentGetStarted.text }}
        </template>
      </OrganismCallToAction>
    </UiComponentContainer>

    <!-- Section > SEO Items -->
    <UiComponentContainer
      v-if="props.page.seoItems.length > 0"
      class="seo-items"
      :start="1"
      :end="1"
    >
      <UiComponentWrapper class="list">
        <UiComponentWrapper
          v-for="{ id, title, titleLevel, text, image, video } in props.page.seoItems"
          :key="`seo-item-${id}`"
          class="item"
        >
          <UiComponentHeading
            :level="titleLevel || 2"
          >
            {{ title }}
          </UiComponentHeading>
          <div
            class="text"
            v-html="$md.render(text)"
          />
          <UiComponentWrapper
            v-if="image"
            class="image"
          >
            <UiComponentWrapper class="inner">
              <UiComponentImage
                :key="`seo-item-${id}-image-${seoItemImageSize}`"
                :alt="image.alt"
                :expected-size="seoItemImageSize"
                :height="image.height"
                :orientation="image.width >= image.height ? 'landscape' : 'portrait'"
                :source="image.url"
                :width="image.width"
              />
            </UiComponentWrapper>
          </UiComponentWrapper>
          <UiComponentWrapper
            v-if="video && video.provider === 'youtube'"
            class="video"
          >
            <UiComponentWrapper class="inner">
              <iframe
                :src="`https://www.youtube.com/embed/${video.providerUid}`"
                title="YouTube video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerpolicy="strict-origin-when-cross-origin"
                allowfullscreen
              />
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Related Posts -->
    <OrganismHighlightedPosts
      v-if="props.page.relatedPostsItems.length >= 4 || props.componentRelatedPosts.items.length > 0"
      class="related-posts"
      :items="
        props.page.relatedPostsItems.length >= 4
          ? props.page.relatedPostsItems
          : props.componentRelatedPosts.items
      "
      :minutes-read="props.componentGlobal.minRead"
    >
      {{ props.page.relatedPostsTitle || props.componentRelatedPosts.title }}
    </OrganismHighlightedPosts>

    <!-- Section > Our Guides -->
    <OrganismHighlightedGuides
      v-if="props.allGuides.length > 0 || props.page.ourGuidesItems.length > 0"
      class="our-guides"
      :items="
        props.page.ourGuidesItems.length > 0
          ? props.page.ourGuidesItems
          : props.allGuides
      "
    >
      {{ props.page.ourGuidesTitle || props.componentOurGuides.title }}
    </OrganismHighlightedGuides>
  </UiComponentWrapper>
</template>

<script setup>
// Composables
const { track } = useAnalyticsJS()
const { locale } = useI18n()
const localePath = useLocalePath()
const { isLogged } = useIsLoggedIn()
const { $md } = useMarkdown()
const { setResource } = useResource()
const { public: publicRuntimeConfig } = useRuntimeConfig()
const { width: screenWidth } = useScreenSize()

// Props
const props = defineProps({
  allCategories: {
    required: true,
    type: Array,
  },
  allFilters: {
    required: true,
    type: Array,
  },
  allGuides: {
    required: true,
    type: Array,
  },
  allIcebreakers: {
    required: true,
    type: Array,
  },
  componentGetStarted: {
    required: true,
    type: Object,
  },
  componentGlobal: {
    required: true,
    type: Object,
  },
  componentIcebreakerQuestions: {
    required: true,
    type: Object,
  },
  componentOurGuides: {
    required: true,
    type: Object,
  },
  componentRelatedPosts: {
    required: true,
    type: Object,
  },
  componentStepByStep: {
    required: true,
    type: Object,
  },
  page: {
    required: true,
    type: Object,
  },
  parentPage: {
    required: true,
    type: Object,
  },
})

// Refs
const $input = ref(null)
const activeFilters = ref([props.page.tag])
const searchedTerm = ref('')
const sortingOptions = ref([
  { value: 'new', name: props.componentGlobal.mostRecent },
  { value: 'alphabetical-desc', name: 'A-Z' },
  { value: 'alphabetical-asc', name: 'Z-A' },
])
const sortingValue = ref('new')

// Computed Properties
const allMatchingIcebreakers = computed(() => {
  return [...props.allIcebreakers]
    .filter(({ tags }) => activeFilters.value.length === 0
      ? true
      : tags.some(tag => activeFilters.value.includes(tag)),
    )
    .filter(({ title, text }) => searchedTerm.value.trim() === ''
      ? true
      : [title, text].some(content => content.toLowerCase().includes(searchedTerm.value.trim().toLowerCase())),
    )
    .sort((a, b) => {
      switch (sortingValue.value) {
        case 'alphabetical-desc':
          return a.title.localeCompare(b.title)
        case 'alphabetical-asc':
          return b.title.localeCompare(a.title)
        default:
          return b.updatedAt > a.updatedAt
      }
    })
})

const heroImage = computed(() => {
  return props.page.heroImage || props.parentPage.heroImage
})

const heroImageSize = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return screenWidth.value - 48
    default:
      return 620
  }
})

const livestormFeatures = computed(() => {
  return [props.componentGlobal.noCreditCard, props.componentGlobal.noInstall]
})

const requestDemoCallToAction = computed(() => {
  return {
    action: 'Demo clicked',
    slot: props.componentGlobal.ctaGetDemo,
    to: '#form-demo',
  }
})

const seoItemImageSize = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return Math.min(Math.max(screenWidth.value - 48, 1), 840)
    default:
      return 840
  }
})

const sidebarCTACard = computed(() => {
  return props.parentPage.sidebarCtaCard
    ? {
        id: props.parentPage.sidebarCtaCard.id,
        backgroundImage: props.parentPage.sidebarCtaCard.backgroundImage,
        callToAction: props.parentPage.sidebarCtaCard.resourceType.cta,
        coMarketing: false,
        description: props.parentPage.sidebarCtaCard.shortDescription,
        event: {
          name: 'Offer clicked',
          attributes: {
            label: 'Sidebar section CTA',
          },
        },
        image: props.parentPage.sidebarCtaCard.image,
        tagColor: props.parentPage.sidebarCtaCard.resourceType.color,
        tagIcon: props.parentPage.sidebarCtaCard.resourceType.icon,
        title: props.parentPage.sidebarCtaCard.shortTitle,
        to: props.parentPage.sidebarCtaCard.slug.includes('http') ? props.parentPage.sidebarCtaCard.slug : '#form-ebook',
        topic: props.parentPage.sidebarCtaCard.resourceTopic.name,
        trackName: props.parentPage.sidebarCtaCard.trackName || props.parentPage.sidebarCtaCard.trackNameFallback,
        type: props.parentPage.sidebarCtaCard.resourceType.name,
        url: localePath({
          name: 'file-slug',
          params: {
            slug: props.parentPage.sidebarCtaCard.ebook?.slug,
          },
        }),
      }
    : null
})

const signupOrUpgradeCallToAction = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: props.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: props.componentGlobal.ctaGetStartedForFree,
      }
})

const sortingIndex = computed(() => {
  return sortingOptions.value.findIndex(({ value }) => value === sortingValue.value)
})

// Watchers
watch(
  () => searchedTerm.value,
  (value) => {
    track('Term searched', {
      category: 'Search',
      label: 'Icebreaker games search',
      searched_term: value,
      locale: locale.value,
    })
  },
)

// Methods
function navigateToIcebreakerQuestions(event) {
  event.preventDefault()
  if (!(event.target.classList.contains('button') || event.target.classList.contains('icon'))) {
    event.currentTarget.querySelector('.metadata .button').click()
  }
}

function resetSearchedTerm() {
  searchedTerm.value = ''

  $input.value.setValue(searchedTerm.value)
}

function toogleFilter(value) {
  if (activeFilters.value.includes(value)) {
    activeFilters.value = activeFilters.value.filter(filter => filter !== value)
  }
  else {
    activeFilters.value.push(value)
  }
}

function updateSearchedTerm() {
  searchedTerm.value = $input.value.getValue().trim()
}

function updateSorting(value) {
  sortingValue.value = value
}

// Lifecycle Kooks
onMounted(() => {
  setResource({
    ...sidebarCTACard.value,
    event: {
      name: 'Offer clicked',
      attributes: {
        label: 'Thank you pop up CTA',
      },
    },
    to: sidebarCTACard.value.url,
  })
})
</script>

<style lang="postcss" scoped>
.page {
  @apply flex flex-col items-center;

  .jumbotron {
    @apply flex-col mt-6;

    @screen xl {
      @apply flex-row flex-wrap items-center justify-between;
    }

    .hero {
      @screen xl {
        @apply w-span6 items-start;
      }

      :deep(.h1),
      :deep(p) {
        @screen xl {
          @apply text-left;
        }
      }

      :deep(.button-list) {
        @screen xl {
          @apply justify-start;
        }
      }
    }

    .illustration {
      @apply mt-6 rounded-none;

      @screen xl {
        @apply mt-0 w-span6;
      }
    }
  }

  .search {
    @apply justify-center mt-14 border-t border-b border-grey-blue-200;

    @screen md {
      @apply mt-16;
    }

    .inner {
      @apply flex-col gap-4 py-6;

      @screen md {
        @apply gap-6;
      }

      @screen xl {
        @apply flex-row flex-wrap items-center gap-10;
      }

      .h2 {
        @apply font-medium text-origin leading-7;

        @screen mdminus {
          @apply text-xl;
        }

        @screen mdplus {
          @apply text-xl;
        }

        @screen xl {
          @apply w-span3;
        }
      }

      .bar {
        @apply relative gap-4;

        @screen xl {
          @apply w-span9;
        }

        input {
          @apply pl-12;

          @screen xl {
            @apply max-w-[556px];
          }
        }

        .icon-search {
          @apply absolute left-4 top-1/2 text-xl text-grey-blue-400 leading-none transform -translate-y-1/2;
        }

        .icon-x {
          @apply absolute right-20 top-1/2 text-xl text-grey-blue-400 leading-none opacity-0 invisible cursor-pointer transform -translate-y-1/2 transition-all duration-500 ease-in-out;

          &.active {
            @apply opacity-100 visible;
          }
        }

        .button {
          @apply flex-shrink-0 h-12 w-12 p-0 bg-grey-blue-200;

          i {
            @apply text-grey-blue-600;
          }
        }

        .select {
          @apply hidden;

          @screen md {
            @apply flex flex-shrink-0 w-auto ml-6 bg-grey-blue-100 rounded z-0;

            @screen xl {
              @apply ml-auto;
            }

            :deep(.icon-chevron-down) {
              @apply hidden;

              @screen md {
                @apply flex;
              }
            }

            :deep(.icon-filter) {
              @apply left-auto;

              @screen md {
                @apply left-3;
              }
            }

            :deep(.icon-filter),
            :deep(.icon-chevron-down) {
              &::before {
                @apply text-sm;
              }
            }

            :deep(select) {
              @apply font-medium text-grey-blue-900 border-none;
            }
          }
        }
      }
    }
  }

  .main {
    @apply flex-col mt-14;

    @screen md {
      @apply mt-16;
    }

    @screen xl {
      @apply flex-row flex-wrap gap-10;
    }

    .head {
      @apply flex-col gap-8;

      @screen xl {
        @apply w-span3;
      }

      .filters {
        @apply flex-col bg-white border-2 border-grey-blue-200 rounded-4xl;

        .title {
          @apply items-center justify-between p-6 pb-4;

          .remove {
            @apply font-medium text-livestorm-blue-700 text-sm leading-6;
          }
        }

        .types {
          @apply flex-col;

          .type {
            @apply flex-col gap-4 p-6 border-t-2 border-grey-blue-200;

            .name {
              @apply font-medium text-winter-green-900 text-sm leading-6 capitalize;
            }

            .list {
              @apply flex-wrap gap-4;

              .item {
                @apply flex items-center w-auto px-2 py-1 bg-livestorm-blue-100 font-medium text-livestorm-blue-700 text-xs leading-4 rounded-xs cursor-pointer;

                &.active {
                  @apply bg-livestorm-blue-700 text-white;
                }

                &.selected {
                  @apply gap-1 bg-livestorm-blue-700 text-white;

                  &::before {
                    @apply font-icon text-white text-[10px] leading-none content-["\e961"];
                  }
                }
              }
            }
          }
        }
      }

      .resource {
        @apply hidden;

        @screen xl {
          @apply flex flex-col h-auto;

          :deep(.head) {
            @apply h-60;
          }

          :deep(.body) {
            @apply p-6;

            .tag-with-image {
              @apply gap-1 h-6 px-2 font-medium text-xs leading-4;

              i {
                @apply m-0 text-xs leading-none;
              }
            }

            .description {
              @apply mt-2;
            }

            .button {
              &.text {
                @apply mt-2 pt-4 pb-2 text-origin leading-none;
              }
            }
          }
        }
      }

      .questions {
        @apply hidden;

        @screen xl {
          @apply flex flex-col bg-white border-2 border-grey-blue-200 rounded-3xl cursor-pointer overflow-hidden transition-shadow ease-out duration-500;
        }

        &:hover {
          @apply shadow-light4;
        }

        .illustration {
          @apply justify-center h-[202px] bg-grey-blue-100;

          picture {
            @apply rounded-none;
          }
        }

        .metadata {
          @apply flex-col gap-2 p-6 pb-4;

          .button {
            @apply self-start h-[50px] px-0 font-bold text-livestorm-blue-700;

            i {
              @apply text-livestorm-blue-700;
            }
          }
        }
      }
    }

    .body {
      @apply flex-col mt-10 gap-10;

      @screen md {
        @apply flex-row flex-wrap;
      }

      @screen xl {
        @apply self-start w-span9 mt-0;
      }

      .card {
        @screen md {
          width: calc((100% - 40px) / 2);
        }

        @screen xl {
          width: calc((100% - 80px) / 3);
        }
      }

      .empty {
        @apply justify-center;

        .inner {
          @apply flex-col items-center gap-4 max-w-[400px] pt-10;

          .illustration {
            @apply rounded-none;
          }

          p {
            @apply text-center;

            & + p {
              @apply text-sm text-grey-blue-600 leading-6;
            }
          }
        }
      }
    }

    .footer {
      @apply flex-col gap-14 mt-14;

      @screen xl {
        @apply hidden;
      }

      .resource {
        @apply flex flex-col h-auto;

        @screen md {
          @apply flex-row h-[343px];
        }

        :deep(.head) {
          @screen lg {
            @apply h-[343px] w-[343px];
          }
        }

        :deep(.body) {
          @apply p-6;

          .tag-with-image {
            @apply gap-1 h-6 px-2 font-medium text-xs leading-4;

            i {
              @apply m-0 text-xs leading-none;
            }
          }

          .description {
            @apply mt-2;
          }

          .button {
            &.text {
              @apply mt-2 pt-4 pb-2 text-origin leading-none;
            }
          }
        }
      }

      .questions {
        @apply flex flex-col bg-white border-2 border-grey-blue-200 rounded-3xl overflow-hidden;

        @screen md {
          @apply flex-row items-center h-[343px];
        }

        .illustration {
          @apply justify-center h-[202px] bg-grey-blue-100;

          @screen md {
            @apply items-end max-w-[343px] h-full overflow-hidden;
          }

          picture {
            @apply rounded-none;
          }
        }

        .metadata {
          @apply flex-col gap-2 p-6 pb-4;

          .button {
            @apply self-start h-[50px] px-0 font-bold text-livestorm-blue-700;

            i {
              @apply text-livestorm-blue-700;
            }
          }
        }
      }
    }
  }

  .use-cases {
    @apply flex flex-col gap-10 mt-26;

    @screen md {
      @apply gap-14 mt-46;
    }

    .h2 {
      @apply self-center text-center;

      @screen md {
        @apply self-start text-left;
      }

      @screen xl {
        @apply self-center text-center;
      }
    }

    .list {
      @apply flex-wrap justify-center gap-4;

      .item {
        @apply px-4 py-1 font-bold text-livestorm-blue-700 text-sm leading-6;
      }
    }
  }

  .how-to {
    @apply flex flex-col gap-12 mt-26;

    @screen md {
      @apply mt-46;
    }

    @screen xl {
      @apply items-center;
    }

    .list {
      @apply flex-col gap-10;

      @screen xl {
        @apply flex-row flex-nowrap;
      }

      .item {
        @apply flex-col gap-6;

        @screen md {
          @apply flex-row flex-nowrap;
        }

        @screen xl {
          @apply flex-col flex-wrap items-center;
        }

        .head {
          @apply flex-shrink-0 items-center justify-center h-20 w-20 bg-winter-green-100 rounded-3xl;

          .icon {
            @apply text-winter-green-700 text-2xl leading-none;
          }
        }

        .body {
          @apply flex-col gap-4;

          .h3 {
            @apply text-xl leading-7;

            @screen mdminus {
              @apply text-2xl leading-8;
            }

            @screen mdplus {
              @apply text-2xl leading-8;
            }

            @screen xl {
              @apply text-center;
            }
          }

          p {
            @apply text-grey-blue-600;

            @screen xl {
              @apply text-center;
            }
          }
        }
      }
    }
  }

  .get-started {
    @apply mt-26;

    @screen md {
      @apply mt-46;
    }
  }

  .seo-items {
    @apply mt-26;

    @screen md {
      @apply mt-46;
    }

    .list {
      @apply flex-col;

      .item {
        @apply flex-col mt-6;

        &:first-child {
          @apply mt-0;
        }

        :deep(.text) {
          @apply mt-1 text-grey-blue-600 text-base leading-[26px];

          a {
            @apply text-livestorm-blue-700;
          }

          b,
          strong {
            @apply font-medium text-grey-blue-600;
          }

          ol {
            li {
              @apply font-normal text-grey-blue-600 text-base leading-[26px] list-decimal list-inside;
            }
          }

          ul {
            li {
              @apply block font-normal text-grey-blue-600 text-base leading-[26px];

              &:before {
                @apply inline-block w-3 h-3 bg-livestorm-blue-700 mr-3;
                border-radius: 3px;
                content: "";
              }
            }
          }

          p + p,
          p + ol,
          p + ul,
          ol + ol,
          ol + p,
          ul + ul,
          ul + p {
            @apply mt-1;
          }
        }

        .image {
          @apply justify-center mt-6;

          .inner {
            @apply relative max-w-[840px] bg-grey-blue-100 rounded-4xl overflow-hidden;
          }
        }

        .video {
          @apply justify-center mt-6;

          .inner {
            @apply relative max-w-[840px] bg-grey-blue-100 aspect-video rounded-4xl overflow-hidden;

            iframe {
              @apply absolute h-full w-full flex;
            }
          }
        }
      }
    }
  }

  .related-posts {
    @apply mt-14;

    @screen md {
      @apply mt-26;
    }
  }
}
</style>
